import React from 'react';

import { Badge, type BadgeProps } from '@/design-system/badge';
import {
  DELIVERY_STATUS_LABELS,
  DeliveryStatus,
} from '@/services/api/deliveries';

type BadgeStatus = NonNullable<BadgeProps['status']>;

// Mirrors next-backoffice-web `DeliveryStatusBadge.jsx` color decisions, mapped
// onto the Dash kit Badge palette:
//   danger→error  info→information  amber→away  warning→warning
//   success→success  neutral→neutral  primary→feature  rose→highlighted
const STATUS_TONE: Record<DeliveryStatus, BadgeStatus> = {
  PENDING_PAYMENT: 'error',
  EXPIRED: 'neutral',
  QUEUEING: 'neutral',
  ALLOCATING: 'information',
  PENDING_PICKUP: 'away',
  PICKING_UP: 'warning',
  ARRIVED_AT_PICKUP_POINT: 'neutral',
  CREATED: 'information',
  PREPARING: 'information',
  PENDING_DELIVERY: 'away',
  IN_DELIVERY: 'warning',
  ARRIVED_AT_DESTINATION: 'neutral',
  VERIFIED: 'success',
  NOT_VERIFIED: 'neutral',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'neutral',
  IN_RETURN: 'feature',
  PENDING_RETURN: 'highlighted',
  FAILED_IN_RETURN: 'error',
  RETURN_TO_HUB: 'neutral',
  ARRIVED_AT_HUB: 'neutral',
  ON_HOLD: 'neutral',
  ARRIVED_AT_RETURN_POINT: 'neutral',
  RETURNED: 'information',
  DISPOSED: 'error',
  REJECTED: 'neutral',
};

export const DeliveryStatusBadge: React.FC<{ status: DeliveryStatus }> = ({
  status,
}) => (
  <Badge status={STATUS_TONE[status] ?? 'neutral'} type="dot">
    {DELIVERY_STATUS_LABELS[status] ?? status}
  </Badge>
);

export const SlaPill: React.FC<{ status: 'ON_TIME' | 'LATE' | null }> = ({
  status,
}) => {
  if (status === 'ON_TIME') {
    return (
      <span className="text-[12px] font-semibold tracking-tight text-(--state-success-dark) tabular-nums">
        On-time
      </span>
    );
  }
  if (status === 'LATE') {
    return (
      <span className="text-[12px] font-semibold tracking-tight text-error-base tabular-nums">
        Late
      </span>
    );
  }
  return <span className="text-[12px] text-text-sub-600">—</span>;
};
